import { useEffect, useState } from "react";

import {
  getTaylorSeriesResultJson,
  normalizeTaylorSeriesCenter,
  normalizeTaylorSeriesOrder,
  type TaylorSeriesSelection,
} from "@/input/utils/taylorSeriesAction";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";

type ReadySelection = Extract<TaylorSeriesSelection, { type: "ready" }>;

type Params = {
  previewDoc: DocumentModel;
  selection: ReadySelection;
  variableName: string;
  center: number;
  order: number;
};

export function useTaylorSeriesPreview({
  previewDoc,
  selection,
  variableName,
  center,
  order,
}: Params): { preview: NodeModel[]; isLoading: boolean; equals: boolean } {
  const [preview, setPreview] = useState<NodeModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [equals, setEquals] = useState(false);

  const selectionExpr = selection.expr;
  const variableKey = selection.variableNames.join("|");

  useEffect(() => {
    const normalizedCenter = normalizeTaylorSeriesCenter(center);
    const normalizedOrder = normalizeTaylorSeriesOrder(order);

    if (
      normalizedCenter === null ||
      normalizedOrder === null ||
      !selection.variableNames.includes(variableName)
    ) {
      setPreview([]);
      setEquals(false);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    getTaylorSeriesResultJson(selection, {
      variableName,
      center: normalizedCenter,
      order: normalizedOrder,
    })
      .then((result) => {
        if (isCancelled) return;

        const nodes = previewDoc.transact(() => {
          return (result?.nodes ?? [])
            .map((json) => nodeFromJson(previewDoc, json, { resetIds: true }))
            .filter((node): node is NodeModel => node !== undefined);
        }, "TaylorSeriesPreview");

        setPreview(nodes);
        setEquals(result?.equals ?? false);
      })
      .catch(() => {
        if (!isCancelled) {
          setPreview([]);
          setEquals(false);
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [center, order, previewDoc, selectionExpr, variableKey, variableName]); // eslint-disable-line react-hooks/exhaustive-deps

  return { preview, isLoading, equals };
}
